import { ReactElement, useState } from "react";
import { Combobox as PasteCombobox } from "@twilio-paste/core/combobox";
import { Box } from "@twilio-paste/core/box";
import { Button } from "@twilio-paste/core/button";
import { SearchIcon } from "@twilio-paste/icons/cjs/SearchIcon";
import {
  ComboboxBlock,
  Item,
  trackingDetails,
} from "@twiliointernal/e2-commons-shared-definitions";
import { useWiseOwlContext } from "../../../hooks";

interface ComboboxRendererProps {
  element?: string;
  block: ComboboxBlock;
}

const Combobox = ({ element, block }: ComboboxRendererProps): ReactElement => {
  const {
    chatId,
    handlers: { handleSendChatMessage, handleTrackEvent },
    applicationProps: { displayName, ingress },
  } = useWiseOwlContext();

  const [inputItems, setInputItems] = useState(block.values.items);

  const [selectedItem, setSelectedItem] = useState<Item>(inputItems[0]);

  const handleCancelClicked = () => {
    const message = "Nevermind, I don't want make this selection.";
    if (handleSendChatMessage) {
      const data = {
        chatId: chatId,
        input: message,
      };
      handleSendChatMessage(data);
      handleComboboxTrackingEvent(false, "user canceled combobox selection");
    }
  };

  const handleSubmitClicked = () => {
    if (selectedItem && handleSendChatMessage) {
      const message = `Account ${selectedItem.name} with ID ${selectedItem.id} selected.`;
      const data = {
        chatId: chatId,
        input: message,
      };
      handleSendChatMessage(data);
      handleComboboxTrackingEvent(true, selectedItem.displayName);
    }
  };

  const handleItemChanged = (changes: any) => {
    const { selectedItem } = changes;
    const selectedItemObj = inputItems.find(
      (item) => item.id === selectedItem.id
    );
    if (selectedItemObj) {
      setSelectedItem(selectedItemObj);
    }
  };

  const handleComboboxTrackingEvent = (
    isSubmit: boolean,
    itemName: string
  ): void => {
    if (handleTrackEvent) {
      handleTrackEvent(
        trackingDetails.wiseOwl.comboboxButtonClicked(
          displayName,
          isSubmit,
          itemName
        ),
        {
          chatId,
          ingress,
        }
      );
    }
  };

  return (
    <Box
      as="div"
      padding="space50"
      borderWidth="borderWidth10"
      borderStyle="solid"
      borderRadius="borderRadius20"
      borderColor="colorBorderDecorative10Weaker"
    >
      <PasteCombobox
        autocomplete
        labelText={block.values.label}
        items={inputItems}
        initialSelectedItem={selectedItem}
        optionTemplate={(item) => item.displayName}
        itemToString={(item) => (item ? item.displayName : null)}
        onInputValueChange={({ inputValue }) => {
          if (inputValue !== undefined) {
            setInputItems(
              block.values.items.filter((item) =>
                item.displayName
                  .toLowerCase()
                  .startsWith(inputValue.toLowerCase())
              )
            );
          }
        }}
        insertAfter={
          <Box display="flex">
            <SearchIcon
              decorative={false}
              size="sizeIcon20"
              title="Search Accounts"
            />
          </Box>
        }
        onSelectedItemChange={handleItemChanged}
      />
      <Box display="flex" justifyContent="space-between" marginTop="space50">
        <Button onClick={handleCancelClicked} variant="secondary">
          Cancel
        </Button>
        <Button onClick={handleSubmitClicked} variant="primary">
          Submit
        </Button>
      </Box>
    </Box>
  );
};

export default Combobox;
